import React, { useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Star, CheckCircle, ArrowRight, MessageCircle, Phone } from 'lucide-react';
import { BRAND, PACKAGES } from '../utils/constants';
import useCountUp from '../hooks/useCountUp';
import useSEO from '../hooks/useSEO';
import './Home.css';

const IgIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
  </svg>
);

/* ── Reveal wrapper ───────────────────────────── */
function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); } },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
}

/* ── Animated counter ─────────────────────────── */
function StatCounter({ target, suffix = '', label }) {
  const { count, ref } = useCountUp(target);
  return (
    <div className="proof-item" ref={ref}>
      <span className="proof-num">{count}{suffix}</span>
      <span className="proof-label">{label}</span>
    </div>
  );
}

/* ── Before / After card ──────────────────────── */
const TRANSFORMS = [
  { img: '/images/transform-3.jpg', label: 'Lost the belly. Built the confidence.', tag: 'Online Training' },
  { img: '/images/transform-1.jpg', label: 'Dropped two sizes in 3 months.', tag: 'In-Person' },
  { img: '/images/transform-2.jpg', label: 'Total body recomp. No surgery. Just work.', tag: 'In-Person' },
  { img: '/images/transform-4.jpg', label: 'Same person. Different energy.', tag: 'Online Training' },
  { img: '/images/transform-5.jpg', label: 'From slimmaz to thickaz. Not just weight loss — body recomposition.', tag: 'In-Person' },
  { img: '/images/transform-6.jpg', label: 'Showed up. Put in the work. Got the results.', tag: 'In-Person' },
];

/* ── Google Reviews ───────────────────────────── */
const GOOGLE_REVIEWS = [
  {
    name: 'Karlease Brown',
    date: '3 months ago',
    stars: 5,
    text: "I have been using this gym since May and so far I have lost 20 lbs. I am a very picky eater and he aligns my meal plans to the things that I consume. The gym is like a small community where we look out for each other and give guidance and support. Results are seen right around.",
    avatar: 'K',
  },
  {
    name: 'Venesha Reid',
    date: '2 years ago',
    stars: 5,
    text: "I have been struggling with my weight for years especially after I gave birth to my son. Fearon makes it feel easy to do AS LONG AS YOU STICK TO THE PLAN!!! I have lost over 20 lbs and I have regained my confidence! This man works with you and gives you the diet that suits. SIGN UP!!! Thank me later.",
    avatar: 'V',
  },
  {
    name: 'Danica',
    date: '3 months ago',
    stars: 5,
    text: "As a PCOS girly, the weight was hard for me to take off however Trainer Fearon is very attentive and dedicated to getting the best out of you. From the daily quotes and jokes, you'll be short of nothing at the Fhysique Factory. No regrets joining.",
    avatar: 'D',
  },
  {
    name: 'Lamoine Samuels-Lee',
    date: '3 months ago',
    stars: 5,
    text: "Been at it for over 6 weeks now and I am seeing results — feeling lighter, more energized and getting stronger. The instructor is like a Backra Massa but the results are impeccable!!! Loving it.",
    avatar: 'L',
  },
  {
    name: 'Martina Thompson',
    date: '2 months ago',
    stars: 5,
    text: "Trust me, just come with an open mind and follow his guidance, and you'll see results. The environment is very welcoming, and the workouts are engaging. Fearon is a trainer who truly cares about your overall health, not just your physique, and that's always a win!",
    avatar: 'M',
  },
];

export default function Home() {
  const heroRef = useRef(null);

  const faqSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Do I need gym experience to join The Fhysique Factory?', acceptedAnswer: { '@type': 'Answer', text: 'No. Ramone works with all fitness levels — complete beginners to advanced athletes. Your program is built around where you are right now.' } },
      { '@type': 'Question', name: 'How much does a personal trainer cost in Kingston Jamaica?', acceptedAnswer: { '@type': 'Answer', text: 'The Fhysique Factory offers online training from $10,000 JMD/month, in-person training from $15,000 JMD/month, and drop-in classes at $1,500 JMD per session. A 1-on-1 consultation is $2,000 JMD.' } },
      { '@type': 'Question', name: 'How does online fitness training work in Jamaica?', acceptedAnswer: { '@type': 'Answer', text: 'You get a custom workout and meal plan, access to the private accountability group, bi-weekly check-ins via WhatsApp, and live morning and evening sessions Monday through Friday.' } },
      { '@type': 'Question', name: 'What are the gym session times at The Fhysique Factory?', acceptedAnswer: { '@type': 'Answer', text: 'In-person sessions run Mon/Tue/Thu/Fri at 5am, 6am, 7am, 6pm and 7:15pm. Wednesday has morning sessions only. Saturday drop-in classes at 7am.' } },
      { '@type': 'Question', name: 'How quickly will I see results with a personal trainer?', acceptedAnswer: { '@type': 'Answer', text: 'Most clients see measurable changes within 4–6 weeks when they follow the program consistently. Several have lost 15–22 lbs in 6 weeks.' } },
      { '@type': 'Question', name: 'Where is The Fhysique Factory located in Kingston?', acceptedAnswer: { '@type': 'Answer', text: 'The Fhysique Factory is located at 6-8 Parkington Plaza, Maths Unlimited Building, Kingston, Jamaica.' } },
      { '@type': 'Question', name: 'Does The Fhysique Factory offer meal plans?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Every training package includes a custom meal plan built around Jamaican food culture and your dietary preferences. Ramone adjusts plans based on your restrictions and goals.' } },
      { '@type': 'Question', name: 'What payment methods does The Fhysique Factory accept?', acceptedAnswer: { '@type': 'Answer', text: 'All major cards and PayPal via secure online booking. No cash handling — everything is tracked to your unique client reference number.' } },
    ],
  }), []);

  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://fysiquefactory.fit/' },
    ],
  }), []);

  useSEO({
    title: '#1 Gym in Kingston Jamaica | Personal Training & Fitness Classes | The Fhysique Factory',
    description: 'The #1 gym in Kingston, Jamaica. Lose weight, build muscle & transform your body with Ramone Fearon at The Fhysique Factory. Gym classes, online & in-person training from $10,000 JMD/month. 500+ transformations. Book your free trial today.',
    path: '/',
    schema: [faqSchema, breadcrumbSchema],
  });

  useEffect(() => {
    const lines = heroRef.current?.querySelectorAll('.hero-line');
    lines?.forEach((el, i) => {
      el.style.animationDelay = `${0.05 + i * 0.1}s`;
      el.classList.add('line-reveal');
    });
  }, []);

  return (
    <div className="page home-page">

      {/* ══════════════════════════════════════
          HERO — trainer photo as background
      ══════════════════════════════════════ */}
      <section className="hero" ref={heroRef}>
        <div className="hero-img-bg">
          <img src="/images/hero-bg.jpg" alt="The Fhysique Factory gym" fetchpriority="high" />
          <div className="hero-img-overlay" />
        </div>
        <div className="hero-grid-bg" />
        <div className="hero-glow-br" />

        <div className="container hero-inner">
          <div className="hero-content">
            <p className="hero-eyebrow hero-line">
              <span className="eyebrow-dot" />
              Kingston's #1 Gym · Parkington Plaza · Est. 2021
            </p>

            <h1 className="hero-h1">
              <span className="hero-line hero-word">Build</span>
              <span className="hero-line hero-word hero-word--indent">Your</span>
              <span className="hero-line hero-word">
                Dream <em className="lime-italic">Fhysique</em>
              </span>
            </h1>

            <p className="hero-sub hero-line">
              Group training, custom meal plans, and real accountability —
              <br />designed for people who are actually ready to change.
            </p>

            <div className="hero-urgency hero-line">
              <span className="urgency-badge">
                <span className="urgency-dot" />
                Only 3 spots left this month
              </span>
            </div>

            <div className="hero-ctas hero-line">
              <Link to="/book" className="btn btn-primary btn-lg" style={{ position: 'relative', overflow: 'hidden' }}>
                Book Now — From $2,000 <ArrowRight size={16} />
              </Link>
              <Link to="/packages" className="btn btn-ghost btn-lg">
                View Packages
              </Link>
            </div>

            <div className="hero-trust hero-line">
              {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="var(--lime)" color="var(--lime)" />)}
              <span>5.0 on Google · 190+ Active Clients · Limited Spots</span>
            </div>
          </div>
        </div>

        <div className="hero-scroll-hint">
          <span>SCROLL</span>
          <div className="scroll-track"><div className="scroll-thumb" /></div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          PROOF STRIP
      ══════════════════════════════════════ */}
      <section className="proof-strip">
        <div className="container proof-inner">
          <StatCounter target={190} suffix="+" label="Active Clients" />
          <div className="proof-divider" />
          <StatCounter target={500} suffix="+" label="Transformations" />
          <div className="proof-divider" />
          <StatCounter target={5} label="Google Rating" suffix="★" />
          <div className="proof-divider" />
          <StatCounter target={2021} label="Est. Year" />
        </div>
      </section>

      {/* ══════════════════════════════════════
          MEET THE TRAINER
      ══════════════════════════════════════ */}
      <section className="trainer-section">
        <div className="container trainer-grid">
          <Reveal className="trainer-img-wrap">
            <img src="/images/trainer-standing.jpg" alt="Ramone Fearon, Head Coach" />
            <div className="trainer-badge">
              <span className="trainer-badge-num">5+</span>
              <span className="trainer-badge-label">Years Coaching</span>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="trainer-copy">
            <span className="section-label">Your Coach</span>
            <h2 className="section-title">Ramone Fearon</h2>
            <p className="trainer-bio">
              Ramone Fearon built The Fhysique Factory because Kingston needed more than a
              generic gym — it needed a coach who actually stays in your corner.
            </p>
            <p className="trainer-bio">
              Certified personal trainer, nutrition specialist, and strength coach with 5+ years
              transforming bodies across Jamaica. He doesn't hand you a plan and disappear —
              he checks in twice a week, adjusts when life happens, and holds you to it.
            </p>
            <div className="trainer-certs">
              {['Personal Trainer', 'Nutrition Specialist', 'Strength & Conditioning'].map(c => (
                <span key={c} className="cert-chip"><CheckCircle size={12} />{c}</span>
              ))}
            </div>
            <Link to="/book" className="btn btn-ghost">
              Book a Session <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════
          PACKAGES
      ══════════════════════════════════════ */}
      <section className="home-packages">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-label">Pricing</span>
            <h2 className="section-title">No Gimmicks.<br />Just Results.</h2>
            <p className="section-sub">
              Three programs. All built around your goals.
              {' '}<span className="text-lime">Spots fill up — don't sleep on it.</span>
            </p>
          </Reveal>
          <div className="packages-grid">
            {PACKAGES.map((pkg, i) => (
              <Reveal key={pkg.id} delay={i * 0.1}>
                <div className={`pkg-card ${pkg.color === 'accent' ? 'featured' : ''}`}>
                  {pkg.badge && <div className="pkg-badge">{pkg.badge}</div>}
                  <div className="pkg-name">{pkg.name}</div>
                  <div className="pkg-price">{pkg.price}<span>{pkg.period}</span></div>
                  {pkg.note && <p className="pkg-note">{pkg.note}</p>}
                  <div className="pkg-divider" />
                  <ul className="pkg-features">
                    {pkg.features.map((f, j) => (
                      <li key={j}><CheckCircle size={13} /> {f}</li>
                    ))}
                  </ul>
                  <Link to={`/book?package=${pkg.id}`} className={`btn btn-full ${pkg.color === 'accent' ? 'btn-primary' : 'btn-ghost'}`}>
                    {pkg.cta} <ArrowRight size={15} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          TRANSFORMATIONS
      ══════════════════════════════════════ */}
      <section className="transforms-section">
        <div className="transforms-bg" />
        <div className="container">
          <Reveal className="section-head">
            <span className="section-label">Before &amp; After</span>
            <h2 className="section-title">Real People.<br />Real Work.<br />Real Change.</h2>
            <p className="section-sub">
              Every single one of these clients showed up, put in the work, and got results.
              No filters. No surgery. Just consistency and the right program.
            </p>
          </Reveal>
          <div className="transforms-grid">
            {TRANSFORMS.map((t, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div className="transform-card">
                  <div className="transform-img-wrap">
                    <img src={t.img} alt={t.label} loading="lazy" />
                    <span className="transform-tag">{t.tag}</span>
                  </div>
                  <p className="transform-label">{t.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="transforms-cta">
            <Link to="/contact" className="btn btn-primary btn-lg">
              Start Your Transformation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          GOOGLE REVIEWS
      ══════════════════════════════════════ */}
      <section className="reviews-section">
        <div className="container">
          <Reveal className="section-head reviews-head">
            <div className="google-badge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Google Reviews</span>
            </div>
            <h2 className="section-title">What Kingston<br />Is Saying</h2>
            <div className="google-overall">
              <span className="google-score">5.0</span>
              <div>
                <div className="google-stars">
                  {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="#FBBC05" color="#FBBC05" />)}
                </div>
                <span className="google-count">Based on 40+ Google reviews</span>
              </div>
            </div>
          </Reveal>

          <div className="reviews-grid">
            {GOOGLE_REVIEWS.map((r, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="review-card">
                  <div className="review-header">
                    <div className="review-avatar">{r.avatar}</div>
                    <div className="review-meta">
                      <strong>{r.name}</strong>
                      <span className="review-date">{r.date}</span>
                    </div>
                    <svg className="google-g" width="18" height="18" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  </div>
                  <div className="review-stars">
                    {[...Array(r.stars)].map((_, s) => <Star key={s} size={13} fill="#FBBC05" color="#FBBC05" />)}
                  </div>
                  <p className="review-text">"{r.text}"</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="reviews-cta">
            <a
              href="https://www.google.com/maps/search/The+Fhysique+Factory+Kingston+Jamaica"
              target="_blank" rel="noopener noreferrer"
              className="btn btn-outline"
            >
              See All Google Reviews
            </a>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          SOCIAL BAR
      ══════════════════════════════════════ */}
      <section className="social-bar">
        <div className="container social-bar-inner">
          <span className="social-bar-label">Follow the journey</span>
          <a href={`https://instagram.com/${BRAND.instagram}`} target="_blank" rel="noopener noreferrer" className="social-bar-btn instagram">
            <IgIcon size={16} /> @{BRAND.instagram}
          </a>
          <a href={`https://wa.me/${BRAND.whatsapp}?text=Hi! I'm interested in joining The Fhysique Factory.`} target="_blank" rel="noopener noreferrer" className="social-bar-btn whatsapp">
            <MessageCircle size={16} /> WhatsApp
          </a>
          <a href={`tel:${BRAND.phone}`} className="social-bar-btn call">
            <Phone size={16} /> {BRAND.phoneDisplay}
          </a>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FAQ
      ══════════════════════════════════════ */}
      <section className="faq-section">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-label">FAQ</span>
            <h2 className="section-title">Common Questions</h2>
          </Reveal>
          <div className="faq-grid">
            {[
              { q: 'Do I need gym experience to join?', a: 'No. Ramone works with all fitness levels — complete beginners to advanced athletes. Your program is built around where you are right now, not where you think you should be.' },
              { q: 'What\'s included in the consultation?', a: 'A private 60-minute session with Ramone covering a full fitness and goal assessment, personalized program recommendation, and a sample meal plan overview. The $2,000 JMD fee is credited toward any monthly plan you choose.' },
              { q: 'How does online training work?', a: 'You get a custom workout and meal plan, access to the private accountability group, bi-weekly check-ins with Ramone via WhatsApp, and access to morning and evening live sessions Monday, Tuesday, Thursday, and Friday.' },
              { q: 'What are the in-person session times?', a: 'In-person sessions run Mon/Tue/Thu/Fri at 5am, 6am, 7am, 6pm and 7:15pm. Wednesday has morning sessions only. Saturday drop-in classes at 7am.' },
              { q: 'Can I do both online and in-person?', a: 'The In-Person package includes everything in Online Training plus 5 days of physical coaching at Parkington Plaza — it\'s the best of both.' },
              { q: 'How quickly will I see results?', a: 'Most clients see measurable changes within 4–6 weeks when they follow the program consistently. Several have lost 15–22 lbs in 6 weeks. Results depend on consistency, diet, and starting point.' },
              { q: 'What if I have dietary restrictions or health conditions?', a: 'Ramone builds meal plans around your lifestyle, food preferences, and any restrictions. Let him know during your consultation and the plan will be adjusted accordingly.' },
              { q: 'What payment methods are accepted?', a: 'All major cards and PayPal via our secure online booking. No cash handling — everything is tracked to your unique client reference number.' },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <div className="faq-item">
                  <h4 className="faq-q">{item.q}</h4>
                  <p className="faq-a">{item.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════ */}
      <section className="final-cta">
        <div className="final-cta-grid-bg" />
        <div className="container final-cta-inner">
          <img src="/images/logo-dark.jpg" alt="The Fhysique Factory" className="cta-logo" />
          <h2>Stop Waiting.<br /><span className="text-lime">Start Now.</span></h2>
          <p>
            Ramone Fearon has helped over 500 people transform their bodies in Kingston.
            Your spot is available — but it won't stay open long.
          </p>
          <div className="final-cta-btns">
            <Link to="/book" className="btn btn-primary btn-lg">
              Buy Now — Start from $2,000 <ArrowRight size={18} />
            </Link>
            <Link to="/packages" className="btn btn-ghost btn-lg">
              See All Packages
            </Link>
          </div>
          <p className="final-cta-contact">{BRAND.phoneDisplay} · @{BRAND.instagram}</p>
        </div>
      </section>

    </div>
  );
}
